import { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  canonical?: string;
  schema?: object;
}

const SEOHead = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  canonical,
  schema
}: SEOHeadProps) => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://wotech.tr';
  const faviconUrl = 'https://static.readdy.ai/image/9c916c921dab6b7efa0ebb131a6ff24b/485db2f12095dd86fc0d8598315d6ff3.png';

  useEffect(() => {
    if (title) {
      document.title = title;
    }

    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);
    if (ogTitle) updateMetaTag('og:title', ogTitle, true);
    if (ogDescription) updateMetaTag('og:description', ogDescription, true);

    if (canonical) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `${siteUrl}${canonical}`);
    }

    updateMetaTag('og:url', `${siteUrl}${canonical || '/'}`, true);

    if (schema) {
      let script = document.querySelector('script[data-schema="page"]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.setAttribute('data-schema', 'page');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify({
        ...schema,
        url: `${siteUrl}${canonical || '/'}`
      });
    }

    // Organization Schema
    if (!document.querySelector('script[data-schema="organization"]')) {
      const orgScript = document.createElement('script');
      orgScript.setAttribute('type', 'application/ld+json');
      orgScript.setAttribute('data-schema', 'organization');
      orgScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": ["Organization", "ProfessionalService"],
        "name": "WOTECH",
        "alternateName": "WOTECH Bilgi Teknolojileri",
        "description": "ManageEngine, AutomMate ve Zoho ONE ürünlerinde uzmanlaşmış kurumsal IT danışmanlık ve çözüm şirketi. 12+ yıl deneyim, 400+ başarılı proje.",
        "url": siteUrl,
        "logo": {
          "@type": "ImageObject",
          "url": faviconUrl,
          "width": 512,
          "height": 512
        },
        "image": faviconUrl,
        "foundingDate": "2012",
        "numberOfEmployees": {
          "@type": "QuantitativeValue",
          "minValue": 10
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "TR",
          "addressLocality": "Türkiye"
        },
        "areaServed": {
          "@type": "Country",
          "name": "Türkiye"
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "availableLanguage": ["Turkish", "English"]
        },
        "knowsAbout": [
          "ManageEngine",
          "AutomMate",
          "Zoho ONE",
          "ITSM",
          "ServiceDesk Plus",
          "Endpoint Central",
          "Active Directory",
          "IT Otomasyon",
          "UEM",
          "Kurumsal IT Çözümleri"
        ],
        "sameAs": [
          "https://linkedin.com/company/wotech",
          "https://twitter.com/wotech"
        ]
      });
      document.head.appendChild(orgScript);
    }

    // WebSite Schema
    if (!document.querySelector('script[data-schema="website"]')) {
      const wsScript = document.createElement('script');
      wsScript.setAttribute('type', 'application/ld+json');
      wsScript.setAttribute('data-schema', 'website');
      wsScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "WOTECH",
        "url": siteUrl,
        "description": "ManageEngine, AutomMate ve Zoho ONE Türkiye Partneri. Kurumsal IT çözümleri.",
        "inLanguage": "tr-TR",
        "publisher": {
          "@type": "Organization",
          "name": "WOTECH"
        }
      });
      document.head.appendChild(wsScript);
    }
  }, [title, description, keywords, ogTitle, ogDescription, canonical, schema, siteUrl]);

  return null;
};

export default SEOHead;
